// ModificationAnalytics.ts - Calculate meaningful statistics from API modifications data

export interface ModificationData {
  modification_id: number;
  user_id: number;
  user_name: string;
  timestamp: string;
  isnegative: boolean;
  modification_detail: string;
  modification_amount: number;
  mesa_id: number;
  workstation_id: number | null;
  approver_id: number;
  modification_metadata: any;
  local_id: number;
  session_id: number | null;
}

export interface UserStats {
  user_id: number;
  user_name: string;
  negative_count: number;
  positive_count: number;
  total_negative_amount: number;
  total_positive_amount: number;
  avg_negative_amount: number;
  avg_positive_amount: number;
  needs_approval_count: number; // when user_id !== approver_id
}

export interface ItemStats {
  item_id: number;
  item_name: string;
  modification_count: number;
  negative_count: number;
  positive_count: number;
  total_amount_impact: number;
}

export interface DailySummary {
  date: string;
  total_modifications: number;
  negative_modifications: number;
  positive_modifications: number;
  total_monetary_impact: number;
  negative_monetary_impact: number;
  positive_monetary_impact: number;
}

export interface ModificationAnalytics {
  // User-based insights
  top_negative_users: UserStats[];
  top_positive_users: UserStats[];
  users_needing_approval: UserStats[];
  
  // Item-based insights  
  most_modified_items: ItemStats[];
  
  // Monetary insights
  total_negative_impact: number;
  total_positive_impact: number;
  net_impact: number;
  avg_daily_negative_impact: number;
  avg_daily_positive_impact: number;
  
  // Volume insights
  total_modifications: number;
  negative_modifications: number;
  positive_modifications: number;
  avg_daily_modifications: number;
  
  // Today's performance
  today_negative_count: number;
  today_positive_count: number;
  today_negative_amount: number;
  today_positive_amount: number;
  
  // Daily breakdown
  daily_summary: DailySummary[];
}

export function calculateModificationAnalytics(
  modifications: ModificationData[], 
  startDate: string, 
  endDate: string
): ModificationAnalytics {
  
  // Helper: Get today's date in YYYY-MM-DD format
  const today = new Date().toISOString().split('T')[0];
  
  // Helper: Extract date from timestamp
  const extractDate = (timestamp: string): string => {
    return new Date(timestamp).toISOString().split('T')[0];
  };

  // Filter modifications within date range
  const filteredModifications = modifications.filter(mod => {
    const modDate = extractDate(mod.timestamp);
    return modDate >= startDate && modDate <= endDate;
  });

  // Separate by positive/negative
  const negativeModifications = filteredModifications.filter(mod => mod.isnegative);
  const positiveModifications = filteredModifications.filter(mod => !mod.isnegative);
  
  // Today's modifications
  const todayNegative = negativeModifications.filter(mod => extractDate(mod.timestamp) === today);
  const todayPositive = positiveModifications.filter(mod => extractDate(mod.timestamp) === today);

  // Calculate user statistics
  const userStatsMap = new Map<number, UserStats>();
  
  filteredModifications.forEach(mod => {
    if (!userStatsMap.has(mod.user_id)) {
      userStatsMap.set(mod.user_id, {
        user_id: mod.user_id,
        user_name: mod.user_name,
        negative_count: 0,
        positive_count: 0,
        total_negative_amount: 0,
        total_positive_amount: 0,
        avg_negative_amount: 0,
        avg_positive_amount: 0,
        needs_approval_count: 0,
      });
    }
    
    const userStats = userStatsMap.get(mod.user_id)!;
    
    if (mod.isnegative) {
      userStats.negative_count++;
      userStats.total_negative_amount += Math.abs(mod.modification_amount);
    } else {
      userStats.positive_count++;
      userStats.total_positive_amount += mod.modification_amount;
    }
    
    if (mod.user_id !== mod.approver_id) {
      userStats.needs_approval_count++;
    }
  });

  // Calculate averages for users
  userStatsMap.forEach(userStats => {
    userStats.avg_negative_amount = userStats.negative_count > 0 
      ? userStats.total_negative_amount / userStats.negative_count 
      : 0;
    userStats.avg_positive_amount = userStats.positive_count > 0 
      ? userStats.total_positive_amount / userStats.positive_count 
      : 0;
  });

  const userStatsArray = Array.from(userStatsMap.values());

  // Sort users by negative impact
  const topNegativeUsers = userStatsArray
    .filter(user => user.negative_count > 0)
    .sort((a, b) => b.total_negative_amount - a.total_negative_amount);
  
  const topPositiveUsers = userStatsArray
    .filter(user => user.positive_count > 0)
    .sort((a, b) => b.total_positive_amount - a.total_positive_amount);
    
  const usersNeedingApproval = userStatsArray
    .filter(user => user.needs_approval_count > 0)
    .sort((a, b) => b.needs_approval_count - a.needs_approval_count);

  // Calculate item statistics - only considering negative modifications for financial impact
  const itemStatsMap = new Map<string, ItemStats>();
  
  filteredModifications.forEach(mod => {
    // Extract item name from modification_detail (assuming format like "Eliminar: ItemName" or similar)
    const itemName = mod.modification_detail.includes(':') 
      ? mod.modification_detail.split(':')[1]?.trim() || mod.modification_detail
      : mod.modification_detail;
    
    const itemKey = `${itemName}_${mod.local_id}`; // Use local_id as item identifier
    
    if (!itemStatsMap.has(itemKey)) {
      itemStatsMap.set(itemKey, {
        item_id: mod.local_id,
        item_name: itemName,
        modification_count: 0,
        negative_count: 0,
        positive_count: 0,
        total_amount_impact: 0,
      });
    }
    
    const itemStats = itemStatsMap.get(itemKey)!;
    itemStats.modification_count++;
    
    if (mod.isnegative) {
      itemStats.negative_count++;
      // Only add negative amounts to financial impact
      itemStats.total_amount_impact += Math.abs(mod.modification_amount);
    } else {
      itemStats.positive_count++;
      // Don't add positive amounts to financial impact
    }
  });

  const mostModifiedItems = Array.from(itemStatsMap.values())
    .filter(item => item.negative_count > 0) // Only show items with negative modifications
    .sort((a, b) => b.total_amount_impact - a.total_amount_impact); // Sort by financial impact

  // Calculate daily summaries
  const dailySummaryMap = new Map<string, DailySummary>();
  
  filteredModifications.forEach(mod => {
    const date = extractDate(mod.timestamp);
    
    if (!dailySummaryMap.has(date)) {
      dailySummaryMap.set(date, {
        date,
        total_modifications: 0,
        negative_modifications: 0,
        positive_modifications: 0,
        total_monetary_impact: 0,
        negative_monetary_impact: 0,
        positive_monetary_impact: 0,
      });
    }
    
    const summary = dailySummaryMap.get(date)!;
    summary.total_modifications++;
    
    if (mod.isnegative) {
      summary.negative_modifications++;
      summary.negative_monetary_impact += Math.abs(mod.modification_amount);
      // Only negative modifications count toward total monetary impact
      summary.total_monetary_impact += Math.abs(mod.modification_amount);
    } else {
      summary.positive_modifications++;
      summary.positive_monetary_impact += mod.modification_amount;
      // Positive modifications don't count toward financial impact
    }
  });

  const dailySummary = Array.from(dailySummaryMap.values())
    .sort((a, b) => a.date.localeCompare(b.date));

  // Calculate totals and averages - only negative modifications for financial impact
  const totalNegativeImpact = negativeModifications.reduce((sum, mod) => sum + Math.abs(mod.modification_amount), 0);
  const totalPositiveImpact = positiveModifications.reduce((sum, mod) => sum + mod.modification_amount, 0);
  const daysInRange = dailySummary.length || 1; // Avoid division by zero

  const todayNegativeCount = todayNegative.length;
  const todayPositiveCount = todayPositive.length;
  const todayNegativeAmount = todayNegative.reduce((sum, mod) => sum + Math.abs(mod.modification_amount), 0);
  const todayPositiveAmount = todayPositive.reduce((sum, mod) => sum + mod.modification_amount, 0);

  return {
    // User insights
    top_negative_users: topNegativeUsers,
    top_positive_users: topPositiveUsers,
    users_needing_approval: usersNeedingApproval,
    
    // Item insights
    most_modified_items: mostModifiedItems,
    
    // Monetary insights
    total_negative_impact: totalNegativeImpact,
    total_positive_impact: totalPositiveImpact,
    net_impact: totalPositiveImpact - Math.abs(totalNegativeImpact),
    avg_daily_negative_impact: totalNegativeImpact / daysInRange,
    avg_daily_positive_impact: totalPositiveImpact / daysInRange,
    
    // Volume insights
    total_modifications: filteredModifications.length,
    negative_modifications: negativeModifications.length,
    positive_modifications: positiveModifications.length,
    avg_daily_modifications: filteredModifications.length / daysInRange,
    
    // Today's performance
    today_negative_count: todayNegativeCount,
    today_positive_count: todayPositiveCount,
    today_negative_amount: todayNegativeAmount,
    today_positive_amount: todayPositiveAmount,
    
    // Daily breakdown
    daily_summary: dailySummary,
  };
}

// Format currency helper
export function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('es-CO', {
    style: 'currency',
    currency: 'COP',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(Math.abs(amount));
}
